import TabSwitchComponent from 'components/atoms/TabSwitch/TabSwitch';
import { useContext, useMemo, useState } from 'react';
import { tabsType } from 'types/pricingSimulator';
import ChangeAnalyticsContainer from './ChangeAnalyticsContainer';
import CategoryMovementContainer from './CategoryMovementContainer';
import { CustomSelect } from '../DimSection';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import TierAnalyticsContainer from './TierAnalyticsContainer';
import { getConfig } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import { UserContext } from 'contexts/ConfigContext';
import Legend from 'components/molecules/Legend';
import _ from 'lodash';

const AnalyticsChartContainer = () => {
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const { dim1DropdownList = [], dim2DropdownList = [] } = dimensionConfigData ?? {};
    const {
        selectedReportInfo,
        updateReportProperty,
        applyFilterObject,
        allCheckedAttribute,
        setApplyFilterObject,
        setAllCheckedAttribute,
    } = useContext(PricingAnalyticsDataContext);
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
    const { tierAnalytics } = pricingSimulatorConfig;
    const tierAnalyticsColorPallete = tierAnalytics
        ? Object.values(tierAnalytics.visualConfig).map((item: any) => ({
              label: item.label,
              color: item.tableColor,
          }))
        : [];

    //dropdown list
    const listToDisplay = useMemo(() => {
        return [
            { label: 'Product', options: dim1DropdownList },
            { label: 'Geo', options: dim2DropdownList },
        ];
    }, [dim1DropdownList, dim2DropdownList]);

    const tabs: tabsType[] = [
        {
            key: 'changeAnalytics',
            label: 'Change analytics',
            component: <ChangeAnalyticsContainer />,
        },
        { key: 'categoryMovement', label: 'Category movement', component: <CategoryMovementContainer /> },
        ...(!!tierAnalytics
            ? [
                  {
                      key: 'tierAnalytics',
                      label: tierAnalytics.tabLabel,
                      component: <TierAnalyticsContainer />,
                  },
              ]
            : []),
    ];
    const [activeTab, setActiveTab] = useState(tabs[0]);

    const activeTabComponent = useMemo(() => {
        return tabs.filter((item) => item.key === activeTab.key)?.[0]?.component;
    }, [activeTab]);

    // reset all the filters and add just company dimension
    const applyDefaultFilterForTierView = () => {
        const resetFilterDimension = tierAnalytics?.resetFilterDimension;
        if (resetFilterDimension) {
            const newApplyFilterObj = _.cloneDeep(applyFilterObject);
            const newAllCheckedAttribute = _.cloneDeep(allCheckedAttribute);

            const newSet = new Set();
            newSet.add(resetFilterDimension.defaultFilterDimValue);
            newApplyFilterObj[resetFilterDimension.defaultFilterDimId]['inclusion'] = _.cloneDeep(newSet);
            newAllCheckedAttribute[resetFilterDimension.defaultFilterDimId] = _.cloneDeep(newSet);
            setApplyFilterObject(newApplyFilterObj);
            setAllCheckedAttribute(newAllCheckedAttribute);
        }
    };

    return (
        <div className="mt-6">
            <div className="flex gap-2 justify-between items-center">
                <div className="flex justify-between w-full px-1">
                    <div className="flex gap-2  items-center">
                        <p className="text-blackAlt font-medium font-lg">Reports</p>
                        <TabSwitchComponent
                            activeTabKey={activeTab?.key}
                            onTabChange={function (tab: tabsType): void {
                                setActiveTab(tab);
                                if (tab.key === 'tierAnalytics') {
                                    applyDefaultFilterForTierView();
                                }
                            }}
                            tabs={tabs}
                        />
                    </div>
                    {activeTab.key === 'tierAnalytics' && (
                        <Legend data={tierAnalyticsColorPallete} className="border border-backgroundGreyAlt1" />
                    )}
                </div>
                {activeTab.key === 'changeAnalytics' && (
                    <div className="flex gap-2 items-center">
                        <label className="font-xs">Level</label>
                        <CustomSelect
                            className="dropdown-width-multi-select dropdown-height uniqSelPSCompetitionDropdown"
                            placeholder="Multiple Select"
                            showArrow
                            showSearch={false}
                            value={selectedReportInfo?.changeAnalytics?.input?.[0]?.attributeType?.value}
                            onChange={(val: any, item: any) => {
                                const updatedInput = selectedReportInfo?.changeAnalytics?.input?.map((graphItem) => {
                                    return {
                                        ...graphItem,
                                        attributeType: item,
                                    };
                                });
                                updateReportProperty('changeAnalytics', {
                                    output: selectedReportInfo?.changeAnalytics?.output,
                                    input: updatedInput,
                                });
                            }}
                            options={listToDisplay}
                        />
                    </div>
                )}
            </div>
            {activeTabComponent}
        </div>
    );
};

export default AnalyticsChartContainer;
